import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const DeBroglieWavelengthCalculator = () => {
  const [mass, setMass] = useState('');
  const [velocity, setVelocity] = useState('');
  const [wavelength, setWavelength] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateWavelength = () => {
    if (mass && velocity) {
      const m = parseFloat(mass);
      const v = parseFloat(velocity);
      
      // Validate inputs
      if (isNaN(m) || isNaN(v) || m <= 0 || v <= 0) {
        alert('Please enter valid positive numbers for mass and velocity');
        return;
      }

      const h = 6.62607015e-34; // Planck's constant

      // Calculate wavelength using de Broglie equation: λ = h / (m * v)
      const λ = h / (m * v);
      
      // Format the result with appropriate precision
      const formattedWavelength = λ < 1e-9 ? λ.toExponential(2) : λ.toFixed(15);
      setWavelength(formattedWavelength);
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!mass || !velocity || !wavelength) return null;

    const m = parseFloat(mass);
    const v = parseFloat(velocity);
    const λ = parseFloat(wavelength);

    // Calculate appropriate scale for visualization
    const scale = Math.max(0.01, Math.min(1, λ));
    const numWaves = 3; // Show 3 complete waves
    const totalLength = numWaves * λ;

    // Generate wave-like graph data
    const series = [
      {
        name: 'Wavelength Visualization',
        data: Array.from({ length: 100 }, (_, i) => {
          const x = (i / 99) * totalLength;
          const y = Math.sin(x * (2 * Math.PI / λ)) * 0.5 + 0.5;
          return {
            x,
            y
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        }
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
        colors: ['#4F46E5']
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Position (m)',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#374151'
          }
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Amplitude',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#374151'
          }
        },
        min: 0,
        max: 1,
        labels: {
          formatter: function (val) {
            return val.toFixed(1);
          }
        }
      },
      title: {
        text: 'Wave Function Visualization',
        align: 'center',
        style: {
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#1F2937'
        }
      },
      markers: {
        size: 0,
      },
      annotations: {
        yaxis: [
          {
            y: 0.5,
            borderColor: '#4F46E5',
            label: {
              borderColor: '#4F46E5',
              style: {
                color: '#fff',
                background: '#4F46E5',
                fontSize: '14px'
              },
              text: `Wavelength = ${λ < 1e-9 ? λ.toExponential(2) : λ.toFixed(15)}m`
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      grid: {
        borderColor: '#e5e7eb',
        strokeDashArray: 2,
      },
      background: '#fff',
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.2,
          stops: [0, 100]
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-50 to-purple-50">
      <Helmet>
        <title>de Broglie Wavelength Calculator | Physics Tool</title>
        <meta name="description" content="Calculate the de Broglie wavelength of a particle using its mass and velocity. Interactive calculator with wave visualization showing quantum mechanical properties." />
        <meta name="keywords" content="de Broglie wavelength calculator, physics calculator, quantum mechanics calculator, wavelength calculator" />
        <meta property="og:title" content="de Broglie Wavelength Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate the de Broglie wavelength of a particle using its mass and velocity. Interactive calculator with wave visualization showing quantum mechanical properties." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="de Broglie Wavelength Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate the de Broglie wavelength of a particle using its mass and velocity. Interactive calculator with wave visualization showing quantum mechanical properties." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/de-broglie-wavelength" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">de Broglie Wavelength Calculator</h1>
          <p className="text-xl text-gray-600">Calculate the wavelength of a particle using its mass and velocity</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Mass (m)</label>
                <input
                  type="number"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter mass (kg)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Velocity (v)</label>
                <input
                  type="number"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter velocity (m/s)"
                />
              </div>
              <div>
                <button
                  onClick={calculateWavelength}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Wavelength
                </button>
              </div>

              {mass && velocity && wavelength && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Mass = {mass} kg</p>
                        <p className="text-blue-800">Velocity = {velocity} m/s</p>
                        <p className="text-blue-800">Wavelength = {wavelength} m</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the de Broglie equation:
                      <br />
                      λ = h / (m * v)
                      <br />
                      <br />
                      Where:
                      <br />
                      h = Planck's constant = 6.62607015 × 10⁻³⁴ J·s
                      <br />
                      m = Mass = {mass} kg
                      <br />
                      v = Velocity = {velocity} m/s
                      <br />
                      λ = Wavelength = {wavelength} m
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Wave Function Visualization</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Wave visualization showing the calculated wavelength
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the mass of the particle (in kg)</li>
            <li>Enter the velocity of the particle (in m/s)</li>
            <li>Click "Calculate Wavelength" to get the results</li>
            <li>View the wave visualization</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Quantum Mechanics</h3>
              <p>Calculate the wavelength of any particle using its mass and velocity</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>Visualize the wave function of the particle</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for quantum mechanics and physics students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is de Broglie Wavelength?</h3>
              <p className="text-gray-600">
                de Broglie wavelength is the wavelength associated with a particle of mass m moving at velocity v.
                It's a fundamental concept in quantum mechanics.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Mass should be in kilograms (kg)</li>
                  <li>Velocity should be in meters per second (m/s)</li>
                  <li>The result will be in meters (m)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the wave visualization work?</h3>
              <p className="text-gray-600">
                The wave visualization shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>The wave function of the particle</li>
                  <li>The calculated wavelength as the distance between wave crests</li>
                  <li>Helps visualize the quantum mechanical nature of particles</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                de Broglie wavelength calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Quantum mechanics research</li>
                  <li>Particle physics</li>
                  <li>Nanotechnology</li>
                  <li>Semiconductor physics</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Physicists understand quantum mechanical behavior</li>
                  <li>Students grasp wave-particle duality</li>
                  <li>Teachers demonstrate quantum concepts</li>
                  <li>Researchers in quantum mechanics</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeBroglieWavelengthCalculator;
